import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../config';
import { getToken, setToken, clearToken } from '../utils/api';

const AuthContext = createContext(null);

const FEATURE_FLAG_CLAIMS = [
    'mr_copia_base',
    'mr_copia_direct_rostering',
    'mr_copia_query_approve',
    'mr_copia_ai',
    'mr_copia_occurrence_basic',
    'mr_copia_occurrence_advanced',
    'mr_copia_tagging',
];

/** Maps claim names to camelCase keys (e.g. mr_copia_ai -> copiaAI) */
const parseFeatureFlags = (payload) => {
    const flags = {};
    FEATURE_FLAG_CLAIMS.forEach(claim => {
        const key = claim
            .replace('mr_copia_', 'copia_')
            .replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        flags[key] = payload[claim] != null;
    });
    return flags;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(() => !!getToken());
    const [userId, setUserId] = useState('');
    const [featureFlags, setFeatureFlags] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const existingToken = getToken();
        if (existingToken) {
            setIsAuthenticated(true);
            try {
                const payload = JSON.parse(atob(existingToken.split('.')[1]));
                setUserId(payload.userId || payload.sub || '');
                setFeatureFlags(parseFeatureFlags(payload));
            } catch {
                clearToken();
                setIsAuthenticated(false);
            }
        }
        setLoading(false);
    }, []);

    const authenticateByGuid = useCallback(async (coVal, userVal) => {
        const res = await fetch(`${apiUrl}/ManagerReport/AuthenticateByGuid`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ coVal, userVal }),
        });

        if (res.status === 429) {
            throw new Error('Too many requests. Please wait a moment and try again.');
        }

        if (!res.ok) {
            throw new Error('Link expired or invalid');
        }

        const data = await res.json();
        setToken(data.token);
        setIsAuthenticated(true);
        setUserId(data.userId || '');
        try {
            const payload = JSON.parse(atob(data.token.split('.')[1]));
            setFeatureFlags(parseFeatureFlags(payload));
        } catch { /* token decode failed — flags remain empty */ }
        return data;
    }, []);

    const authenticateByLogin = useCallback(async (username, password) => {
        const res = await fetch(`${apiUrl}/ManagerReport/AuthenticateByLogin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
        });

        if (res.status === 429) {
            throw new Error('Too many login attempts. Please wait a moment and try again.');
        }

        if (!res.ok) {
            throw new Error('Invalid email or password');
        }

        const data = await res.json();
        setToken(data.token);
        setIsAuthenticated(true);
        setUserId(data.userId || '');
        try {
            const payload = JSON.parse(atob(data.token.split('.')[1]));
            setFeatureFlags(parseFeatureFlags(payload));
        } catch { /* token decode failed — flags remain empty */ }
        return data;
    }, []);

    const logout = useCallback(() => {
        clearToken();
        setIsAuthenticated(false);
        setUserId('');
        setFeatureFlags({});
    }, []);

    useEffect(() => {
        const handleExpired = () => {
            clearToken();
            setIsAuthenticated(false);
            setUserId('');
        };
        window.addEventListener('auth:expired', handleExpired);
        return () => window.removeEventListener('auth:expired', handleExpired);
    }, []);

    const value = {
        isAuthenticated,
        userId,
        featureFlags,
        loading,
        authenticateByGuid,
        authenticateByLogin,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
